import JSZip from 'jszip';
import axios from 'axios';
// import dicomParser from 'dicom-parser';
import { saveInstance, getAllInstances, deleteInstance } from './indexedDB.js'; 
// import UploadWorker from '../Workers/upload.worker.js?worker'; // Vite syntax for web worker import

/**
 * Utility class for DICOM instance operations.
 */
export default class Instance {
    /**
     * Store an instance as a ZIP file locally.
     * @param {Object} instanceObj - The instance data.
     * @param {Function} onProgress - Callback for progress updates.
     */
    static async store(instanceObj, onProgress, onSuccess) {
        const zip = new JSZip();
        for (const file of instanceObj.Files) {
            zip.file(file.dest, file.fileData);
        }

        const _instance = { ...instanceObj };
        delete _instance.Files;
        _instance.local = true; // Mark instance as local
        zip.file('instance.json', JSON.stringify(_instance));

        try {
            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(instanceObj.SeriesInstanceUID + '.zip', { create: true });
            const writable = await fileHandle.createWritable();

            const zipBlob = await zip.generateAsync({ type: 'blob' }, (metadata) => {
                if (onProgress) {
                    const progress = Math.round(metadata.percent || 0);
                    onProgress(progress);
                }
            });

            await writable.write(zipBlob);
            await writable.close();

            console.log('Stored zip file locally:', instanceObj.SeriesInstanceUID + '.zip');
        } catch (error) {
            console.error('Error saving zip file:', error);
            return // CANCEL
        }

        try {
            await saveInstance(_instance);
            console.log('Instance metadata saved to IndexedDB:', instanceObj.SeriesInstanceUID);
        } catch (error) {
            console.error('Error saving instance metadata:', error);
            return // CANCEL
        }

        // call onSuccess callback if provided
        if (onSuccess) {
            onSuccess(_instance);
        }
    }

    /**
     * Process an instance by uploading it to the server.
     * @param {Object} instanceObj - The instance data.
     * @param {string} modelName - The name of the model to use for processing.
     * @param {Function} onUploadProgress - Callback for upload progress updates.
     */
    static async process(instanceObj, modelName, onUploadProgress, disabled = false) {


        // just fake upload progress for now
        if (disabled) {
            console.warn('Upload disabled, skipping upload');

            // fire 5 updates to onUploadProgress
            for (let i = 0; i <= 100; i += 20) {
                if (onUploadProgress) {
                    onUploadProgress(i);
                }
                await new Promise(resolve => setTimeout(resolve, Math.random() * 2000 + 1000)); // simulate delay between 1-3 seconds
            }
            return;
        }

        // load local file or create zip file from Files if not empty
        var zipBlob;
        if (instanceObj.Files?.length > 0) {
            console.log('Creating zip file from files');
            const zip = new JSZip();
            for (const file of instanceObj.Files) {
                zip.file(file.dest, file.fileData);
            }
            zipBlob = await zip.generateAsync({ type: 'blob' });
        } else {
            console.log('Loading zip file from local storage');
            try {
                const root = await navigator.storage.getDirectory();
                const fileHandle = await root.getFileHandle(instanceObj.SeriesInstanceUID + '.zip');
                const file = await fileHandle.getFile();
                zipBlob = await file.arrayBuffer();
                zipBlob = new Blob([zipBlob], { type: 'application/zip' });
            }
            catch (error) {
                console.error('Error loading zip file:', error);
                return;
            }
        }

        // if no zip file, raise error
        if (!zipBlob) {
            console.error('No zip file found');
            return;
        }

        // request a task from the server
        const {
            data: { task },
        } = await axios.post('/tasks', {
            SeriesInstanceUID: instanceObj.SeriesInstanceUID,
            model: modelName,
        });

        // debugging
        console.log('Task created:', task);

        // upload the zip file to the server
        // const worker = new UploadWorker();
        // worker.onmessage = (event) => {
        //     const { type, data } = event.data;
        //     if (type === 'progress') {
        //         const progress = data.progress;
        //         if (onUploadProgress) {
        //             onUploadProgress(progress);
        //         }
        //     } else if (type === 'fileProcessed') {
        //         console.log('File processed:', data.fileName, 'Index:', data.index);
        //     } else if (type === 'error') {
        //         console.error('Error processing file:', data.fileName, 'Error:', data.error, 'Index:', data.index);
        //         worker.terminate();
        //     } else if (type === 'done') {
        //         console.log('All files processed successfully');
        //         worker.terminate();
        //     }
        // };

        // worker.postMessage({
        //     type: 'processFiles',
        //     files: [
        //         {
        //             file: zipBlob,
        //             presignedUrl: task.s3_presigned_url,
        //         },
        //     ],
        // });

        if (disabled) {
            console.warn('Upload disabled, skipping upload');
            return;
        } 

        await axios.put(task.s3_presigned_url, zipBlob, {
            headers: {
                'Content-Type': 'application/zip',
            },
            onUploadProgress: (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                onUploadProgress(progress);
            },
        });

        // debugging
        console.log('File uploaded successfully');
    }

    /**
     * Get the total size of the instance in bytes.
     * @param {Object} instanceObj - The instance data.
     * @returns {Promise<number>} The total size in bytes.
     */
    static async getSize(instanceObj) {
        let size;
        if (instanceObj.Files?.length > 0) {
            // Calculate size from Files array
            size = instanceObj.Files.reduce((total, file) => total + (file.fileData?.byteLength || 0), 0);
        } else {
            // Retrieve size from locally stored ZIP file
            try {
                const root = await navigator.storage.getDirectory();
                const fileHandle = await root.getFileHandle(instanceObj.SeriesInstanceUID + '.zip');
                const file = await fileHandle.getFile();
                size = file.size;
            } catch (error) {
                console.error('Error retrieving size from ZIP file:', error);
                size = 0; // Fallback to 0 if there's an error
            }
        }

        return size;
    }

    /**
     * List all locally stored instances.
     * @param {Function} onAddInstance - Callback to handle each instance.
     */
    static async listLocalFiles(onAddInstance) {
        try {
            const instances = await getAllInstances();
            instances.forEach(async (instanceData) => {
                instanceData.local = true; // Mark instance as local
                instanceData.selected = false; // Initialize selected state
                // TODO: consider calculating size here, more complete & simple? But attribute is also saved in db, need to recalc?
                // instanceData.size = await Instance.getSize(instanceData);
                onAddInstance(instanceData);
            });
            console.log('Loaded instances from IndexedDB:', instances);
        } catch (error) {
            console.error('Error loading instances from IndexedDB:', error);
        }
    }

    /**
     * Delete all locally stored instances and their metadata.
     */
    static async deleteAll() {
        try {
            const root = await navigator.storage.getDirectory();
            for await (const entry of root.values()) {
                if (entry.kind === 'file') {
                    console.log('Deleting file:', entry.name);
                    await entry.remove();
                    const seriesInstanceUID = entry.name.replace('.zip', '');
                    await deleteInstance(seriesInstanceUID);
                    console.log('Deleted instance metadata from IndexedDB:', seriesInstanceUID);
                }
            }
        } catch (error) {
            console.error('Error deleting files:', error);
        }
    }

    /**
     * Get all files associated with the instance
     */
    static async getFiles(instanceObj) {
        // tbd
    }

    /**
     * Get all *.dcm files of the instance.
     * TODO: generalize local file management
     */
    static async getDicomFiles(instanceObj) {
        const seriesInstanceUID = instanceObj.SeriesInstanceUID;

        if (!instanceObj.local && instanceObj.Files) {
            console.warn("instance is not locally available, use data from cache")
            return instanceObj.Files.filter(f => f.fileName.endsWith('.dcm')).sort((a, b) => {
                const aNum = parseInt(a.dicomData?.InstanceNumber);
                const bNum = parseInt(b.dicomData?.InstanceNumber);
                if (!isNaN(aNum) && !isNaN(bNum)) {
                    return aNum - bNum;
                }
                return a.fileName.localeCompare(b.fileName);
            }).map(f => {
                return new File([f.fileData], f.fileName, { type: 'application/dicom' });
            })
        }

        const root = await navigator.storage.getDirectory();
        const fileHandle = await root.getFileHandle(seriesInstanceUID + '.zip');
        const zipfile = await fileHandle.getFile();

        if (!zipfile) {
            console.error(`No zip file found for SeriesInstanceUID: ${seriesInstanceUID}`);
            return null;
        }

        // unpack zip file and take only dicom files (*.dcm)
        const zip = await JSZip.loadAsync(zipfile);
        const dicomFiles = Object.keys(zip.files).filter((filename) => filename.endsWith('.dcm'));

        if (dicomFiles.length === 0) {
            console.error(`No DICOM files found in zip for SeriesInstanceUID: ${seriesInstanceUID}`);
            return null;
        }

        // return array of blobs, sorted by file names
        // const blobs = await Promise.all(dicomFiles.sort().map(async (filename) => {
        //     return await zip.files[filename].async('blob');
        // }));

        // return blobs;
        
        const files = await Promise.all(dicomFiles.sort().map(async (filename) => {
            const fileData = await zip.files[filename].async('arraybuffer');
            return new File([fileData], filename, { type: 'application/dicom' });
        }));

        return files;
    }

    /**
     * Download a locally stored instance as a ZIP file.
     * @param {string} seriesInstanceUID - The Series Instance UID of the instance to download.
     */
    static async download(seriesInstanceUID) {
        try {
            const root = await navigator.storage.getDirectory();
            const fileHandle = await root.getFileHandle(seriesInstanceUID + '.zip');
            const file = await fileHandle.getFile();
            const url = URL.createObjectURL(file);
            const a = document.createElement('a');
            a.href = url;
            a.download = seriesInstanceUID + '.zip';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading instance:', error);
        }
    }
}
